<template>
    <Modal
        :isOpen="show && !missingCard.tempHideEditModal.value"
        @close="handleClose()"
        :fullScreenMobile="true"
    >
        <template #title>
            <div
                class="flex flex-col items-center mb-4 sm:mb-0 gap-4 relative overflow-y-auto max-h-[70vh]"
            >
                <ImageLoader>
                    <template #image>
                        <img
                            :src="`${cdnUrl}/${displayedCard?.card_image?.path}/_small/${displayedCard?.card_image?.file_name}`"
                            class="mr-3 float-left max-w-[100px] lg:max-w-[150px] w-full h-auto"
                        />
                    </template>
                    <template #preloader>
                        <div
                            class="mr-3 float-left max-w-[100px] lg:max-w-[150px] w-full h-auto min-h-[60px] min-w-[100px] bg-gray-200 rounded shimmer"
                        ></div>
                    </template>
                    <template #error>
                        <div
                            class="mr-3 float-left max-w-[100px] lg:max-w-[150px] w-full h-auto"
                        >
                            <img src="/images/blank_card.png" class="w-full" />
                        </div>
                    </template>
                </ImageLoader>
                <div class="flex flex-col gap-2 text-slate-800 dark:text-slate-100 font-normal">
                    <h1
                        class="text-xl lg:text-2xl capitalize text-center"
                        v-if="displayedCard"
                    >
                        {{ displayedCard.title }}
                        <span
                            class="text-sm ml-2"
                            v-if="selectedCard?.last_four"
                        >
                            ({{ selectedCard?.last_four }})
                        </span>
                    </h1>
                    <h2 class="text-xl pt-4" v-if="!displayedCard">
                        Match "{{ selectedCard?.name }}"{{
                            selectedCard?.last_four
                                ? ` (${selectedCard?.last_four})`
                                : ''
                        }}
                        to the correct card from the dropdown:
                    </h2>
                    <CardImageSearch
                        :selectedCard="selectedCard"
                        :displayedCard="displayedCard"
                        :changeCard="cardSearch.changeCard.value"
                        :debugCardQuery="cardSearch.debugCardQuery.value"
                        :hasSelectedCard="cardSearch.hasSelectedCard.value"
                        :pendingCardChange="cardSearch.pendingCardChange.value"
                        :cdnUrl="cdnUrl"
                        :debugShowDropdown="cardSearch.debugShowDropdown.value"
                        :dropdownTop="cardSearch.dropdownTop.value"
                        :dropdownLeft="cardSearch.dropdownLeft.value"
                        :dropdownWidth="cardSearch.dropdownWidth.value"
                        :groupedDebugResults="
                            cardSearch.groupedDebugResults.value
                        "
                        @update:changeCard="
                            cardSearch.changeCard.value = $event
                        "
                        @update:debugCardQuery="
                            cardSearch.debugCardQuery.value = $event
                        "
                        @update:debugInputRef="
                            cardSearch.debugInputRef.value = $event
                        "
                        @search="cardSearch.onDebugCardInput()"
                        @focus="cardSearch.onDebugCardFocus()"
                        @select="handleSelectDebugCard"
                        @clear="cardSearch.clearDebugCardQuery()"
                        @cancel="cardSearch.cancelChangeCard()"
                        @apply="applyPendingCardChange"
                        @open-missing-card="missingCard.openMissingCardModal()"
                    />
                </div>
            </div>
        </template>
        <template #content>
            <TransitionGroup name="fade" tag="div" class="relative">
                <div
                    class="flex flex-col gap-6 text-slate-800 dark:text-slate-100"
                    v-if="!localCard.card_not_found || selectedCard"
                    key="card-details"
                >
                    <EditCardForm
                        :localCard="localCard"
                        :selectedCard="selectedCard"
                        :users="users"
                        :businesses="businesses"
                        :wallet="wallet"
                        :errors="validation.errors.value"
                        :selectedUserName="selectedUserName"
                        :selectedBusinessName="selectedBusinessName"
                        :showUserDropdown="showUserDropdown"
                        :showBusinessDropdown="showBusinessDropdown"
                        @clear-error="validation.clearError"
                        @open-add-entity="openAddEntityModal"
                        @toggle-user-dropdown="toggleUserDropdown"
                        @toggle-business-dropdown="toggleBusinessDropdown"
                        @select-user="selectUser"
                        @select-business="selectBusiness"
                    />

                    <section
                        v-if="!localCard.product_changed"
                        class="py-1 rounded-lg sm:space-y-2"
                    >
                        <h2 class="text-base font-medium text-slate-800 dark:text-slate-100">
                            Welcome bonus
                        </h2>
                        <WelcomeBonusForm
                            v-model:welcome-bonus="bonuses.welcomeBonus.value"
                            v-model:tiered-bonuses="
                                bonuses.tieredBonuses.value
                            "
                            :errors="validation.errors.value"
                            @delete="bonuses.handleResetBonuses"
                        />
                    </section>

                    <section class="py-1 rounded-lg sm:space-y-2">
                        <h2 class="text-base font-medium text-slate-800 dark:text-slate-100">
                            Bonus offers
                        </h2>
                        <BonusOffers
                            v-model:bonus-offers="bonuses.bonusOffers.value"
                            :errors="validation.errors.value"
                            @delete="bonuses.resetBonusOffers"
                        />
                    </section>

                    <ProductChangeSection
                        :localCard="localCard"
                        :productChanges="pcHandler.productChanges.value"
                        :welcomeBonus="bonuses.welcomeBonus.value"
                        :tieredBonuses="bonuses.tieredBonuses.value"
                        :errors="validation.errors.value"
                        @update:productChanges="
                            pcHandler.productChanges.value = $event
                        "
                        @update:welcomeBonus="
                            bonuses.welcomeBonus.value = $event
                        "
                        @update:tieredBonuses="
                            bonuses.tieredBonuses.value = $event
                        "
                        @reset-product-changes="handleResetProductChanges"
                        @product-change-deleted="handleProductChangeDeleted"
                        @open-product-change-modal="
                            pcHandler.openProductChangeModal
                        "
                        @edit-product-change="handleEditProductChange"
                        @reset-welcome-bonus="bonuses.handleResetBonuses"
                    />
                </div>
            </TransitionGroup>
        </template>
        <template #errors>
            <div
                class="w-full flex justify-center items-center"
                v-show="validation.errorsList.value.length > 0"
            >
                <Errors :errors="validation.errorsList.value" />
            </div>
        </template>
        <template #footer>
            <div class="flex justify-end gap-2">
                <button
                    type="button"
                    class="px-4 py-2.5 bg-white dark:bg-slate-800 rounded shadow-sm border border-slate-200 dark:border-slate-700 justify-start items-start gap-2 flex text-slate-800 dark:text-slate-100 text-sm font-medium font-['Inter'] leading-tight"
                    @click.prevent="handleClose()"
                >
                    Close
                </button>
                <ActionButton
                    @clicked="saveCardChanges"
                    :label="actionButtonLabel"
                    :loading="isSubmitting"
                    :disabled="hasUnresolvedCardChange"
                    :classes="'px-4 py-2.5 bg-slate-800 rounded shadow-sm justify-center items-center gap-2 flex text-white text-sm font-medium leading-tight'"
                />
            </div>
        </template>
    </Modal>

    <Teleport to="body">
        <ProductChangeModal
            v-if="pcHandler.showProductChangeModal.value"
            :show="pcHandler.showProductChangeModal.value"
            v-model:product-changes="pcHandler.productChanges.value"
            v-model:selected-option="
                pcHandler.selectedProductChangeOption.value
            "
            :cardData="card"
            :editingProductChange="pcHandler.editingProductChange.value"
            :editingProductChangeIndex="
                pcHandler.editingProductChangeIndex.value
            "
            :changedToCard="pcHandler.editingChangedToCard.value"
            :isOriginalProductChange="
                pcHandler.editingIsOriginalProductChange.value
            "
            @close="pcHandler.handleProductChangeModalClose"
            @submit="handleSaveProductChange"
        />
    </Teleport>

    <BusinessEntitiesAddEditModal
        :open="entity.showAddEntityModal.value"
        :users="users"
        v-model:business-model="entity.newEntityModel.value"
        @close="entity.closeAddEntityModal"
        @add-business="entity.handleAddBusiness"
    />

    <MissingCardReport
        :show="missingCard.showMissingCardModal.value"
        :missingCardStep="missingCard.missingCardStep.value"
        v-model:missingCardBank="missingCard.missingCardBank.value"
        v-model:missingCardName="missingCard.missingCardName.value"
        @close="missingCard.closeMissingCardModal"
        @submit="
            missingCard.submitMissingCardRequest(
                localCard.user_id || selectedCard?.user_id
            )
        "
    />
</template>

<script setup>
import { computed, watch, ref, onMounted, onUnmounted, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { useWalletStore } from '@/store/wallet';
import { Apii } from '@/services/api.js';

import { useCardSearch } from '@/composables/useCardSearch';
import { useCardFormValidation } from '@/composables/useCardFormValidation';
import { useProductChanges } from '@/composables/useProductChanges';
import { useMissingCardRequest } from '@/composables/useMissingCardRequest';
import { useBusinessEntity } from '@/composables/useBusinessEntity';
import { useEditCardBonuses } from '@/composables/useEditCardBonuses';

import ActionButton from '@/components/action-button.vue';
import BonusOffers from '@/components/wallet/partials/bonus-offers.vue';
import BusinessEntitiesAddEditModal from '@/components/business-entities/business-entities-add-edit-modal.vue';
import CardImageSearch from '@/components/wallet/modals/edit-card-modal/CardImageSearch.vue';
import EditCardForm from '@/components/wallet/modals/edit-card-modal/EditCardForm.vue';
import Errors from '@/components/errors.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import MissingCardReport from '@/components/wallet/modals/edit-card-modal/MissingCardReport.vue';
import Modal from '@/components/ui/modal/ui-modal.vue';
import ProductChangeModal from '@/components/wallet/modals/product-change-modal.vue';
import ProductChangeSection from '@/components/wallet/modals/edit-card-modal/ProductChangeSection.vue';
import WelcomeBonusForm from '@/components/wallet/partials/welcome-bonus-form.vue';

const ApiService = new Apii();
const walletStore = useWalletStore();
const {
    selectedCard,
    editingProductChange: storeEditingProductChange,
} = storeToRefs(walletStore);
const { showNotification, setEditingProductChange } = walletStore;

const props = defineProps({
    banks: Array,
    card: { type: Object, default: { account_type: 'cardholder', card_status: 'open' } },
    cards: Array,
    show: { type: Boolean, default: false },
    user: Object,
    users: Array,
    wallet: Object,
});

const emit = defineEmits(['close', 'update']);

// Composables
const cardSearch = useCardSearch();
const validation = useCardFormValidation();
const pcHandler = useProductChanges();
const missingCard = useMissingCardRequest();
const entity = useBusinessEntity();
const bonuses = useEditCardBonuses();

const cdnUrl = computed(() => window.cdnUrl);
const isSubmitting = ref(false);

// Local card state
const localCard = ref({
    card_not_found: false,
    card_not_found_notified: false,
    date_received: '',
    date_closed: null,
    credit_limit: null,
});

// Blocks Save Card while the picker has a pending selection that hasn't been applied (✓) or cancelled (×).
const hasUnresolvedCardChange = computed(() => {
    return cardSearch.changeCard.value && cardSearch.hasSelectedCard.value;
});

// Sources the modal header from the in-flight local copy so clicking ✓ updates the title/image before the user saves.
const displayedCard = computed(() => {
    return localCard.value?.card || selectedCard.value?.card || null;
});

// Dropdown states
const showUserDropdown = ref(false);
const showBusinessDropdown = ref(false);
const showAccountTypeDropdown = ref(false);

// Computed
const actionButtonLabel = computed(() => {
    if (localCard.value.card_not_found) {
        if (localCard.value.card_match_request) {
            return localCard.value.card_match_request.rejected
                ? 'Resubmit Match Request'
                : 'Update Match Request';
        }
        return 'Submit Request';
    }
    return 'Save Card';
});

const user = computed(() => {
    return props.wallet.users?.find((el) => el.id === localCard.value.user_id) || { businesses: [] };
});

const businesses = computed(() => {
    return user.value?.businesses?.map((el) => ({ id: el.id, name: el.name })) || [];
});

const selectedUserName = computed(() => {
    return props.wallet.users?.find((u) => u.id === localCard.value.user_id)?.name || '';
});

const selectedBusinessName = computed(() => {
    return businesses.value?.find((b) => b.id === localCard.value.business_id)?.name || '';
});

// Card search handlers
const handleSelectDebugCard = (card) => {
    cardSearch.selectDebugCard(card);
    validation.clearError('card_id');
};

const applyPendingCardChange = () => {
    if (!cardSearch.pendingCardChange.value) return;
    localCard.value.card_id = cardSearch.pendingCardChange.value.id;
    localCard.value.card = cardSearch.pendingCardChange.value;
    validation.clearError('card_id');
    cardSearch.pendingCardChange.value = null;
    cardSearch.changeCard.value = false;
};

// Dropdown handlers
const toggleUserDropdown = () => {
    showUserDropdown.value = !showUserDropdown.value;
    showBusinessDropdown.value = false;
    showAccountTypeDropdown.value = false;
};

const toggleBusinessDropdown = () => {
    showBusinessDropdown.value = !showBusinessDropdown.value;
    showUserDropdown.value = false;
    showAccountTypeDropdown.value = false;
};

const selectUser = (userObj) => {
    localCard.value.user_id = userObj.id;
    showUserDropdown.value = false;
};

const selectBusiness = (business) => {
    localCard.value.business_id = business?.id || '';
    showBusinessDropdown.value = false;
};

const openAddEntityModal = () => {
    const userId = localCard.value.user_id || (props.users?.length === 1 ? props.users[0].id : null);
    entity.openAddEntityModal(userId);
    showBusinessDropdown.value = false;
};

// Product change handlers
const handleEditProductChange = (productChange, index) => {
    pcHandler.editProductChange(productChange, index, localCard.value, props.card);
};

const handleSaveProductChange = (cardData) => {
    pcHandler.saveProductChange(
        cardData, localCard, selectedCard,
        bonuses.bonusOffers, bonuses.originalBonusOffers, bonuses.resetBonusOffers
    );
};

// The last product change was deleted — restore the pre-change card state.
const handleResetProductChanges = (connectedAccount = null) => {
    pcHandler.resetProductChanges(localCard, connectedAccount);
};

// A product change was deleted but others remain — the backend may have
// restored card_id, so adopt its fresh connected_account payload.
const handleProductChangeDeleted = (connectedAccount = null) => {
    pcHandler.applyRestoredCard(localCard, connectedAccount);
};

// Save handler
const saveCardChanges = async (dismissModal = true) => {
    isSubmitting.value = true;
    validation.errors.value = {};
    validation.errorsList.value = [];

    let welcomeBonusData = bonuses.welcomeBonus.value;
    let tieredBonusData = bonuses.tieredBonuses.value;
    let bonusOffersData = [];

    if (bonuses.bonusOffers.value.some((offer) => offer.type)) {
        bonusOffersData = [...bonuses.bonusOffers.value];
    }
    if (
        localCard.value.product_changed &&
        bonuses.originalBonusOffers.value?.some((offer) => offer.type)
    ) {
        bonusOffersData = [...bonusOffersData, ...bonuses.originalBonusOffers.value];
    }
    if (bonusOffersData.length === 0) bonusOffersData = null;
    if (!bonuses.welcomeBonus.value.type) welcomeBonusData = null;
    if (bonuses.welcomeBonus.value.type !== 'tiered') tieredBonusData = [];

    let productChangesData = [];
    if (pcHandler.productChanges.value.some((c) => c.previous_card_id)) {
        productChangesData = [...pcHandler.productChanges.value];
    }

    const data = {
        card: localCard.value,
        welcome_bonus: welcomeBonusData,
        tiered_bonuses: tieredBonusData,
        bonus_offers: bonusOffersData,
        product_changes: productChangesData,
    };

    validation.validateUpdateCardForm(data);
    validation.validateOriginalBonusOffers(
        localCard.value.product_changed, bonuses.originalBonusOffers.value
    );

    if (validation.errorsList.value.length === 0) {
        const url = localCard.value.card_match_request
            ? `/wallets/${walletStore.wallet.id}/cards/${localCard.value.id}/match-request`
            : `/wallets/${walletStore.wallet.id}/cards/${localCard.value.id}`;

        ApiService.apiCall(url, 'put', data)
            .then((response) => {
                if (response.status !== 200) {
                    const error = new Error('Validation error');
                    error.message = response.message;
                    throw error;
                }

                if (localCard.value.card_match_request) {
                    showNotification('Request Updated', "Your card match request has been updated. We'll review the new information.");
                } else if (localCard.value.card_not_found && !localCard.value.card_not_found_notified) {
                    showNotification('Request Sent', "Your Missing Card Request was successfully submitted. You'll hear from us soon.");
                    localCard.value.card_not_found = false;
                } else {
                    showNotification('Card Updated', 'Your card details have been successfully updated.');
                }

                cardSearch.changeCard.value = false;
                isSubmitting.value = false;
                if (dismissModal) handleClose();
                emit('update', { card: response.data.connected_account, user: props.user });
            })
            .catch((error) => {
                isSubmitting.value = false;
                validation.handleSaveError(error);
            });
    } else {
        setTimeout(() => { isSubmitting.value = false; }, 1000);
    }
};

// Reset and close
const resetLocalData = () => {
    cardSearch.changeCard.value = false;
    cardSearch.pendingCardChange.value = null;
    bonuses.handleResetBonuses();
    bonuses.resetBonusOffers();
    pcHandler.resetProductChanges();
    validation.errorsList.value = [];
    validation.errors.value = {};
    localCard.value = {
        card_not_found: false, card_not_found_notified: false,
        date_received: '', credit_limit: null, opened_long_ago: false,
        product_changed: false, product_changed_date_opened: null,
        product_changed_date: null, date_opened: null, date_closed: null,
        original_card: null,
    };
};

const handleClose = () => {
    resetLocalData();
    emit('close', 'showEditCardModal');
};

// Initialize data from selectedCard
const initializeFromSelectedCard = (newVal) => {
    if (!newVal) return;
    localCard.value = { ...newVal };
    localCard.value.date_opened = localCard.value.date_opened || '';
    localCard.value.date_closed = localCard.value.date_closed || '';
    localCard.value.date_received = localCard.value.date_received || '';
    localCard.value.card_status = localCard.value.card_status || 'open';

    if (newVal.product_changes?.length > 0) {
        pcHandler.productChanges.value = [...newVal.product_changes];
        localCard.value.product_changed = true;
    } else {
        pcHandler.resetProductChanges();
    }

    bonuses.initializeBonuses(localCard.value);
};

// Watchers
watch(() => selectedCard.value, (newVal) => initializeFromSelectedCard(newVal), { immediate: true });

watch(() => props.show, (newVal) => {
    if (newVal) {
        initializeFromSelectedCard(selectedCard.value);
        if (storeEditingProductChange.value) {
            nextTick(() => {
                pcHandler.editProductChange(storeEditingProductChange.value, undefined, localCard.value, props.card);
                setEditingProductChange(null);
            });
        }
    }
});

// Click outside handler for dropdowns
const handleClickOutside = (event) => {
    if (!showUserDropdown.value && !showBusinessDropdown.value && !showAccountTypeDropdown.value && !cardSearch.debugShowDropdown.value) return;

    const clickedInCardSearch = cardSearch.debugInputRef.value?.contains(event.target);
    const clickedInCardDropdown = event.target.closest('.fixed.z-\\[99999\\]');
    if (cardSearch.debugShowDropdown.value && !clickedInCardSearch && !clickedInCardDropdown) {
        cardSearch.debugShowDropdown.value = false;
    }

    if (showUserDropdown.value && !event.target.closest('[data-dropdown="user"]') && !event.target.closest('[data-dropdown-options="user"]')) {
        showUserDropdown.value = false;
    }
    if (showBusinessDropdown.value && !event.target.closest('[data-dropdown="business"]') && !event.target.closest('[data-dropdown-options="business"]')) {
        showBusinessDropdown.value = false;
    }
    if (showAccountTypeDropdown.value && !event.target.closest('[data-dropdown="account-type"]') && !event.target.closest('[data-dropdown-options="account-type"]')) {
        showAccountTypeDropdown.value = false;
    }
};

onMounted(() => document.addEventListener('click', handleClickOutside));
onUnmounted(() => document.removeEventListener('click', handleClickOutside));
</script>
